import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { FormGroupWrapper } from 'core/elements';
import { InputType } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { objectMapToSelectOptions, pickByClass } from 'core/utils';
import {
  CorporateSourceType,
  PartPackageTypeLabel,
  PartStatusLabel,
  PartTaaStatusLabel,
} from 'corporate/enums';
import { useUpsertCorporatePart } from 'corporate/hooks';
import { ICorporatePart, UpdateCorporatePartForm } from 'corporate/types';
import { Size } from 'theme/enums';

import { corporatePartUpsertSchema } from './schema';

interface IProps {
  corporatePart?: Partial<ICorporatePart> | null;
  partNumber: string;
  manufacturerId: number;
  isOpen: boolean;
  onClose: () => void;
  // Submit the same field set somewhere other than the single-part upsert — the
  // UOM Conversion Audit uses it to create catalogue entries for a whole
  // selection. Resolving true closes the drawer, exactly as the single-part save
  // does; the caller owns its own notifications.
  onSubmitOverride?: (data: UpdateCorporatePartForm) => Promise<boolean>;
  title?: string;
  subtitle?: string;
  // Sales MOQ and order increment are required when a catalogue entry is being
  // created. The audit page's bulk drawer turns this off for a selection that is
  // only existing parts, so it does not force a value over each of theirs.
  areQuantitiesRequired?: boolean;
}

export const CorporatePartUpsert = ({
  corporatePart,
  partNumber,
  manufacturerId,
  isOpen,
  onClose,
  onSubmitOverride,
  title,
  subtitle,
  areQuantitiesRequired = true,
}: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { upsertCorporatePart } = useUpsertCorporatePart();

  const externalSource = {
    className: corporatePart?.classNameSource === CorporateSourceType.EXTERNAL,
    companyManufacturerName:
      corporatePart?.companyManufacturerNameSource === CorporateSourceType.EXTERNAL,
    companyManufacturerId:
      corporatePart?.companyManufacturerIdSource === CorporateSourceType.EXTERNAL,
  };

  const defaultValues = useMemo(
    () => ({
      ...pickByClass(corporatePart ?? {}, UpdateCorporatePartForm),
      part: partNumber,
      manufacturerId,
    }),
    [corporatePart, partNumber, manufacturerId]
  );

  const corporatePartUom = corporatePart?.uom ? ` ${corporatePart.uom}` : '';

  const onSubmit =
    (reset: UseFormReset<UpdateCorporatePartForm>) => async (data: UpdateCorporatePartForm) => {
      if (onSubmitOverride) {
        if (await onSubmitOverride(data)) {
          // The bulk result (created / updated / skipped counts) renders inside
          // this drawer, and closing clears it -- give it a second to be read,
          // as the other bulk drawers on the audit page do.
          setTimeout(() => handleOnClose(reset), 1000);
        }

        return;
      }

      const { success } = await upsertCorporatePart({
        corporatePart: {
          id: corporatePart?.id,
          ...data,
        },
      });

      if (success) {
        handleOnClose(reset);
      }
    };

  const handleOnClose = (reset: UseFormReset<UpdateCorporatePartForm>) => {
    clearNotification();
    onClose();
    reset(defaultValues);
  };

  return (
    <FormProvider
      schema={corporatePartUpsertSchema(areQuantitiesRequired)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Form
        title={
          title ?? (corporatePart?.id ? t('titleUpdateCompanyPart') : t('titleCreateCompanyPart'))
        }
        subtitle={subtitle}
        size={Size.LG}
        useGridLayout
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      >
        <>
          <FormGroupWrapper name="leftSide" inline={false}>
            <FormField
              name="packageType"
              type={InputType.SELECT}
              inputProps={{
                label: t('labelType'),
                options: objectMapToSelectOptions(PartPackageTypeLabel),
                clearable: true,
              }}
            />
            <FormField
              name="minimumQuantity"
              type={InputType.NUMBER_INPUT}
              inputProps={{
                label: t('labelSalesMinimumOrderQuantity'),
                slotProps: {
                  input: {
                    suffix: corporatePartUom,
                  },
                },
                required: areQuantitiesRequired,
              }}
            />
            <FormField
              name="orderIncrement"
              type={InputType.NUMBER_INPUT}
              inputProps={{
                label: t('labelSalesOrderIncrements'),
                slotProps: {
                  input: {
                    suffix: corporatePartUom,
                  },
                },
                required: areQuantitiesRequired,
              }}
            />
            <FormField
              name="pkgUnits"
              type={InputType.NUMBER_INPUT}
              inputProps={{
                label: t('labelPackageQuantity'),
                slotProps: {
                  input: {
                    suffix: corporatePartUom,
                  },
                },
              }}
            />
            <FormField
              name="bomPriceDecimals"
              type={InputType.NUMBER_INPUT}
              inputProps={{ label: t('labelBomPriceDecimals') }}
            />
            <FormField
              name="quotePriceDecimals"
              type={InputType.TEXT_INPUT}
              inputProps={{ label: t('labelQuotePriceDecimals') }}
            />
            <FormField
              name="manufacturerMinimumQuantity"
              type={InputType.NUMBER_INPUT}
              inputProps={{
                label: t('labelVendorMinimumOrderQuantity'),
                slotProps: {
                  input: {
                    suffix: corporatePartUom,
                  },
                },
              }}
            />
            <FormField
              name="manufacturerOrderIncrement"
              type={InputType.NUMBER_INPUT}
              inputProps={{
                label: t('labelVendorOrderIncrements'),
                slotProps: {
                  input: {
                    suffix: corporatePartUom,
                  },
                },
              }}
            />
            <FormField
              name="className"
              type={InputType.TEXT_INPUT}
              inputProps={{
                label: t('labelClass'),
                disabled: externalSource.className,
                tooltip: externalSource.className ? t('labelExternalSource') : '',
              }}
            />
            <FormField
              name="taaStatus"
              type={InputType.SELECT}
              inputProps={{
                label: t('labelTaaStatus'),
                options: objectMapToSelectOptions(PartTaaStatusLabel),
                clearable: true,
              }}
            />
            <FormField
              name="companyPartStatus"
              type={InputType.SELECT}
              inputProps={{
                label: t('labelCompanyPartStatus'),
                options: objectMapToSelectOptions(PartStatusLabel),
                clearable: true,
              }}
            />
            <FormField
              name="vendorNumber"
              type={InputType.TEXT_INPUT}
              inputProps={{ label: t('labelCompanyPartNumber') }}
            />
            <FormField
              name="companyManufacturerName"
              type={InputType.TEXT_INPUT}
              inputProps={{
                label: t('labelCompanyManufacturerName'),
                disabled: externalSource.companyManufacturerName,
                tooltip: externalSource.companyManufacturerName ? t('labelExternalSource') : '',
              }}
            />
            <FormField
              name="companyManufacturerId"
              type={InputType.TEXT_INPUT}
              inputProps={{
                label: t('labelCompanyManufacturerId'),
                disabled: externalSource.companyManufacturerId,
                tooltip: externalSource.companyManufacturerId ? t('labelExternalSource') : '',
              }}
            />
            <FormField
              name="mfgPartStatus"
              type={InputType.SELECT}
              inputProps={{
                label: t('labelManufacturerPartStatus'),
                options: objectMapToSelectOptions(PartStatusLabel),
                clearable: true,
              }}
            />
            <FormField
              name="replacementPart"
              type={InputType.TEXT_INPUT}
              inputProps={{ label: t('labelReplacementPart') }}
            />
          </FormGroupWrapper>
          <FormGroupWrapper name="rightSide" inline={false}>
            <FormField
              name="partBaseCode"
              type={InputType.TEXT_INPUT}
              inputProps={{ label: t('labelPartBaseCode') }}
            />
            {getPricingGroupFormField({ index: 1, t })}
            {getPricingGroupDescriptionFormField({ index: 1, t })}
            {getPricingGroupFormField({ index: 2, t })}
            {getPricingGroupDescriptionFormField({ index: 2, t })}
            {getPricingGroupFormField({ index: 3, t })}
            {getPricingGroupDescriptionFormField({ index: 3, t })}
            {getPricingGroupFormField({ index: 4, t })}
            {getPricingGroupDescriptionFormField({ index: 4, t })}
            {getPricingGroupFormField({ index: 5, t })}
            {getPricingGroupDescriptionFormField({ index: 5, t })}
          </FormGroupWrapper>
        </>
      </Form>
    </FormProvider>
  );
};

const getPricingGroupFormField = ({ index, t }: { index: number; t: TFunction }) => (
  <FormField
    name={`pricingGroup${index}`}
    type={InputType.TEXT_INPUT}
    inputProps={{ label: t(`labelPricingGroupCode${index}`) }}
  />
);

const getPricingGroupDescriptionFormField = ({ index, t }: { index: number; t: TFunction }) => (
  <FormField
    name={`pricingGroupDescription${index}`}
    type={InputType.TEXT_INPUT}
    inputProps={{ label: t(`labelPricingGroupDescription${index}`) }}
  />
);
